import type { FormEventHandler, MouseEventHandler, ReactNode, RefObject, ChangeEvent } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router';
import {
  Box,
  Button,
  Dialog,
  Header,
  IconButton,
  Input,
  Line,
  Menu,
  MenuItem,
  Text,
  config,
  toRem,
} from 'folds';
import {
  CaretUp,
  Checks,
  composerIcon,
  GearSix,
  Link,
  menuIcon,
  PencilSimple,
  PushPinSlash,
  ShareNetwork,
  UserPlus,
  X,
} from '$components/icons/phosphor';
import type { MatrixClient, Room } from '$types/matrix-sdk';
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import type { Instruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import {
  attachInstruction,
  extractInstruction,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  useOrphanSpaces,
  useRecursiveChildScopeFactory,
  useSpaceChildren,
} from '$state/hooks/roomList';
import { useMatrixClient } from '$hooks/useMatrixClient';
import { roomToParentsAtom } from '$state/room/roomToParents';
import { allRoomsAtom } from '$state/room-list/roomList';
import { getSpaceLobbyPath, getSpacePath, joinPathComponent } from '$pages/pathUtils';
import {
  SidebarAvatar,
  SidebarItemLeft,
  SidebarUnreadBadge,
  SidebarItemTooltip,
  SidebarStack,
  SidebarStackSeparator,
  SidebarFolder,
  SidebarFolderDropTarget,
} from '$components/sidebar';
import { RoomUnreadProvider, RoomsUnreadProvider } from '$components/RoomUnreadProvider';
import { useSelectedSpace } from '$hooks/router/useSelectedSpace';
import { getCanonicalAliasOrRoomId, isRoomAlias, mxcUrlToHttp } from '$utils/matrix';
import { RoomAvatar } from '$components/room-avatar';
import { nameInitials, randomStr } from '$utils/common';
import type { ISidebarFolder, SidebarItems, TSidebarItem } from '$hooks/useSidebarItems';
import {
  makeCinnySpacesContent,
  parseSidebar,
  renameSidebarFolderItem,
  sidebarItemWithout,
  useSidebarItems,
} from '$hooks/useSidebarItems';

import { ScreenSize, useScreenSizeContext } from '$hooks/useScreenSize';
import { useNavToActivePathAtom } from '$state/hooks/navToActivePath';
import { useOpenedSidebarFolderAtom } from '$state/hooks/openedSidebarFolder';
import { usePowerLevels } from '$hooks/usePowerLevels';
import { useRoomsUnread } from '$state/hooks/unread';
import { roomToUnreadAtom } from '$state/room/roomToUnread';
import { markAsRead } from '$utils/notifications';
import { copyToClipboard } from '$utils/dom';
import { shareText } from '$utils/share';
import { getMatrixToRoom } from '$plugins/matrix-to';
import { getViaServers } from '$plugins/via-servers';
import { useMediaAuthentication } from '$hooks/useMediaAuthentication';
import { useRoomAvatar } from '$hooks/useRoomMeta';
import { useSetting } from '$state/hooks/settings';
import { settingsAtom } from '$state/settings';
import { useRoomCreators } from '$hooks/useRoomCreators';
import { useRoomPermissions } from '$hooks/useRoomPermissions';
import { InviteUserPrompt } from '$components/invite-user-prompt';
import { CustomAccountDataEvent } from '$types/matrix/accountData';
import { lastVisitedSpaceIdAtom } from '$state/room/lastSpace';
import { useMobileTapActivation } from '$hooks/useMobileTapActivation';
import { ModalOverlay } from '$components/modal-overlay/ModalOverlay';
import { useOpenRoomSettings } from '$state/hooks/roomSettings';
import { ResponsiveMenu } from '$components/ResponsiveMenu';
import { useMenuAnchor } from '$hooks/useMenuAnchor';

type SpaceMenuProps = {
  room: Room;
  requestClose: () => void;
  onUnpin?: (roomId: string) => void;
};
const SpaceMenu = forwardRef<HTMLDivElement, SpaceMenuProps>(
  ({ room, requestClose, onUnpin }, ref) => {
    const mx = useMatrixClient();
    const [hideReads] = useSetting(settingsAtom, 'hideReads');
    const roomToParents = useAtomValue(roomToParentsAtom);
    const powerLevels = usePowerLevels(room);
    const creators = useRoomCreators(room);

    const permissions = useRoomPermissions(creators, powerLevels);
    const canInvite = permissions.action('invite', mx.getSafeUserId());
    const openRoomSettings = useOpenRoomSettings();

    const [invitePrompt, setInvitePrompt] = useState(false);

    const allChild = useSpaceChildren(
      allRoomsAtom,
      room.roomId,
      useRecursiveChildScopeFactory(mx, roomToParents)
    );
    const unread = useRoomsUnread(allChild, roomToUnreadAtom);

    const handleMarkAsRead = () => {
      allChild.forEach((childRoomId) => markAsRead(mx, childRoomId, hideReads, true));
      requestClose();
    };

    const handleUnpin = () => {
      onUnpin?.(room.roomId);
      requestClose();
    };

    const handleCopyLink = () => {
      const roomIdOrAlias = getCanonicalAliasOrRoomId(mx, room.roomId);
      const viaServers = isRoomAlias(roomIdOrAlias) ? undefined : getViaServers(room);
      copyToClipboard(getMatrixToRoom(roomIdOrAlias, viaServers));
      requestClose();
    };

    const handleShareLink = () => {
      const roomIdOrAlias = getCanonicalAliasOrRoomId(mx, room.roomId);
      const viaServers = isRoomAlias(roomIdOrAlias) ? undefined : getViaServers(room);
      shareText(getMatrixToRoom(roomIdOrAlias, viaServers)).catch(() => {});
      requestClose();
    };

    const handleInvite = () => {
      setInvitePrompt(true);
    };

    const handleRoomSettings = () => {
      openRoomSettings(room.roomId);
      requestClose();
    };

    return (
      <Menu ref={ref} style={{ maxWidth: toRem(160), width: '100vw' }}>
        {invitePrompt && room && (
          <InviteUserPrompt
            room={room}
            requestClose={() => {
              setInvitePrompt(false);
              requestClose();
            }}
          />
        )}
        <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
          <MenuItem
            onClick={handleMarkAsRead}
            size="300"
            after={menuIcon(Checks)}
            radii="300"
            disabled={!unread}
          >
            <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
              Mark as Read
            </Text>
          </MenuItem>
          {onUnpin && (
            <MenuItem size="300" radii="300" onClick={handleUnpin} after={menuIcon(PushPinSlash)}>
              <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
                Unpin
              </Text>
            </MenuItem>
          )}
        </Box>
        <Line variant="Surface" size="300" />
        <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
          <MenuItem
            onClick={handleInvite}
            variant="Primary"
            fill="None"
            size="300"
            after={menuIcon(UserPlus)}
            radii="300"
            aria-pressed={invitePrompt}
            disabled={!canInvite}
          >
            <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
              Invite
            </Text>
          </MenuItem>
          <MenuItem onClick={handleCopyLink} size="300" after={menuIcon(Link)} radii="300">
            <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
              Copy Link
            </Text>
          </MenuItem>
          <MenuItem onClick={handleShareLink} size="300" after={menuIcon(ShareNetwork)} radii="300">
            <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
              Share Link
            </Text>
          </MenuItem>
          <MenuItem onClick={handleRoomSettings} size="300" after={menuIcon(GearSix)} radii="300">
            <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
              Space Settings
            </Text>
          </MenuItem>
        </Box>
      </Menu>
    );
  }
);

type FolderMenuProps = {
  requestClose: () => void;
  onRename: () => void;
};
const FolderMenu = forwardRef<HTMLDivElement, FolderMenuProps>(
  ({ requestClose, onRename }, ref) => (
    <Menu ref={ref} style={{ maxWidth: toRem(160), width: '100vw' }}>
      <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
        <MenuItem
          size="300"
          radii="300"
          onClick={() => {
            onRename();
            requestClose();
          }}
          after={menuIcon(PencilSimple)}
        >
          <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
            Rename
          </Text>
        </MenuItem>
      </Box>
    </Menu>
  )
);

const FOLDER_NAME_MAX_LENGTH = 200;

const folderDefaultDisplayName = (mx: MatrixClient, folder: ISidebarFolder): string => {
  const auto = folder.content.map((i) => mx.getRoom(i)?.name ?? '').join(', ');
  return (folder.name ?? auto) || 'Unnamed';
};

type RenameFolderDialogProps = {
  mx: MatrixClient;
  folder: ISidebarFolder;
  onClose: () => void;
  onSave: (name: string) => void;
};
function RenameFolderDialog({ mx, folder, onClose, onSave }: Readonly<RenameFolderDialogProps>) {
  const [draft, setDraft] = useState(() => folderDefaultDisplayName(mx, folder));

  useEffect(() => {
    setDraft(folderDefaultDisplayName(mx, folder));
  }, [mx, folder]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = (evt) => {
    evt.preventDefault();
    onSave(draft);
  };

  return (
    <ModalOverlay requestClose={onClose}>
      <Dialog variant="Surface">
        <Header
          style={{
            padding: `0 ${config.space.S200} 0 ${config.space.S400}`,
            borderBottomWidth: config.borderWidth.B300,
          }}
          variant="Surface"
          size="500"
        >
          <Box grow="Yes">
            <Text size="H4">Rename Folder</Text>
          </Box>
          <IconButton size="300" onClick={onClose} radii="300">
            {composerIcon(X)}
          </IconButton>
        </Header>
        <Box
          as="form"
          onSubmit={handleSubmit}
          style={{ padding: config.space.S400 }}
          direction="Column"
          gap="400"
        >
          <Text priority="400" size="T300">
            Choose a short label for this folder. Leave empty to show space names again.
          </Text>
          <Box direction="Column" gap="100">
            <Text size="L400">Folder name</Text>
            <Input
              name="folderName"
              variant="Background"
              value={draft}
              maxLength={FOLDER_NAME_MAX_LENGTH}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
              autoFocus
            />
          </Box>
          <Box direction="Row" gap="200" justifyContent="End">
            <Button type="button" variant="Secondary" fill="Soft" onClick={onClose}>
              <Text size="B400">Cancel</Text>
            </Button>
            <Button type="submit" variant="Primary">
              <Text size="B400">Save</Text>
            </Button>
          </Box>
        </Box>
      </Dialog>
    </ModalOverlay>
  );
}

type InstructionType = Instruction['type'];
type FolderDraggable = {
  folder: ISidebarFolder;
  spaceId?: string;
  open?: boolean;
};
type SidebarDraggable = string | FolderDraggable;

const useDraggableItem = (
  item: SidebarDraggable,
  targetRef: RefObject<HTMLElement | null>,
  onDragging: (item?: SidebarDraggable) => void,
  dragHandleRef?: RefObject<HTMLElement | null>,
  enabled: boolean = true
): boolean => {
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const target = targetRef.current;
    const dragHandle = dragHandleRef?.current ?? undefined;

    return target
      ? draggable({
          element: target,
          dragHandle,
          getInitialData: () => ({ item }),
          onDragStart: () => {
            setDragging(true);
            onDragging?.(item);
          },
          onDrop: () => {
            setDragging(false);
            onDragging?.(undefined);
          },
        })
      : undefined;
  }, [targetRef, dragHandleRef, item, onDragging, enabled]);

  return dragging;
};

const useDropTarget = (
  item: SidebarDraggable,
  targetRef: RefObject<HTMLElement | null>,
  enabled: boolean = true
): Instruction | undefined => {
  const [dropState, setDropState] = useState<Instruction>();

  useEffect(() => {
    if (!enabled) return undefined;
    const target = targetRef.current;
    if (!target) return undefined;

    return dropTargetForElements({
      element: target,
      canDrop: ({ source }) => {
        const dragItem = source.data.item as SidebarDraggable;
        return dragItem !== item;
      },
      getData: ({ input, element }) => {
        const block: Instruction['type'][] = ['reparent'];
        if (typeof item === 'object' && item.spaceId) block.push('make-child');

        const insData = attachInstruction(
          {},
          {
            input,
            element,
            currentLevel: 0,
            indentPerLevel: 0,
            mode: 'standard',
            block,
          }
        );

        const instruction: Instruction | null = extractInstruction(insData);
        setDropState(instruction ?? undefined);

        return {
          item,
          instructionType: instruction ? instruction.type : undefined,
        };
      },
      onDragLeave: () => setDropState(undefined),
      onDrop: () => setDropState(undefined),
    });
  }, [item, targetRef, enabled]);

  return dropState;
};

function useDropTargetInstruction<T extends InstructionType>(
  item: SidebarDraggable,
  targetRef: RefObject<HTMLElement | null>,
  instructionType: T,
  enabled: boolean = true
): T | undefined {
  const [dropState, setDropState] = useState<T>();

  useEffect(() => {
    if (!enabled) return undefined;
    const target = targetRef.current;
    if (!target) return undefined;

    return dropTargetForElements({
      element: target,
      canDrop: ({ source }) => {
        const dragItem = source.data.item as SidebarDraggable;
        return dragItem !== item;
      },
      getData: () => {
        setDropState(instructionType);

        return {
          item,
          instructionType,
        };
      },
      onDragLeave: () => setDropState(undefined),
      onDrop: () => setDropState(undefined),
    });
  }, [item, targetRef, instructionType, enabled]);

  return dropState;
}

const useDnDMonitor = (
  scrollRef: RefObject<HTMLElement | null>,
  onDragging: (dragItem?: SidebarDraggable) => void,
  onReorder: (
    draggable: SidebarDraggable,
    container: SidebarDraggable,
    instruction: InstructionType
  ) => void
) => {
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      throw new Error('Scroll element ref not configured');
    }

    return combine(
      monitorForElements({
        onDrop: ({ source, location }) => {
          onDragging(undefined);
          const { dropTargets } = location.current;
          if (dropTargets.length === 0) return;
          const item = source.data.item as SidebarDraggable;
          const containerItem = dropTargets[0]?.data?.item as SidebarDraggable | undefined;
          if (!containerItem) return;
          const instructionType = dropTargets[0]?.data?.instructionType as
            | InstructionType
            | undefined;
          if (!instructionType) return;
          onReorder(item, containerItem, instructionType);
        },
      }),
      autoScrollForElements({
        element: scrollElement,
      })
    );
  }, [scrollRef, onDragging, onReorder]);
};

type SpaceAvatarProps = {
  space: Room;
  renderFallback: () => ReactNode;
};
function SpaceAvatar({ space, renderFallback }: Readonly<SpaceAvatarProps>) {
  const mx = useMatrixClient();
  const useAuthentication = useMediaAuthentication();
  const avatarMxc = useRoomAvatar(space);
  const avatarUrl = avatarMxc
    ? (mxcUrlToHttp(mx, avatarMxc, useAuthentication, 96, 96, 'crop') ?? undefined)
    : undefined;

  return (
    <RoomAvatar
      roomId={space.roomId}
      uniformIcons
      src={avatarUrl}
      alt={space.name}
      renderFallback={renderFallback}
    />
  );
}

type SpaceTabProps = {
  space: Room;
  selected: boolean;
  onSelect: (spaceId: string) => void;
  folder?: ISidebarFolder;
  onDragging: (dragItem?: SidebarDraggable) => void;
  disabled?: boolean;
  onUnpin?: (roomId: string) => void;
};
function SpaceTab({
  space,
  selected,
  onSelect,
  folder,
  onDragging,
  disabled,
  onUnpin,
}: Readonly<SpaceTabProps>) {
  const isMobile = useScreenSizeContext() === ScreenSize.Mobile;
  const targetRef = useRef<HTMLDivElement>(null);
  const menu = useMenuAnchor<HTMLButtonElement>();
  const mobileTapActivation = useMobileTapActivation(
    isMobile,
    () => onSelect(space.roomId),
    () => {
      if (menu.consumeLongPressFired() || menu.anchor) return;
      onSelect(space.roomId);
    }
  );

  const spaceDraggable: SidebarDraggable = useMemo(
    () =>
      folder
        ? {
            folder,
            spaceId: space.roomId,
          }
        : space.roomId,
    [folder, space]
  );

  useDraggableItem(spaceDraggable, targetRef, onDragging, undefined, !isMobile);
  const dropState = useDropTarget(spaceDraggable, targetRef, !isMobile);
  const dropType = dropState?.type;

  return (
    <RoomUnreadProvider roomId={space.roomId}>
      {(unread) => (
        <SidebarItemLeft
          active={selected}
          ref={targetRef}
          aria-disabled={disabled}
          data-drop-child={dropType === 'make-child'}
          data-drop-above={dropType === 'reorder-above'}
          data-drop-below={dropType === 'reorder-below'}
          data-inside-folder={!!folder}
        >
          <SidebarItemTooltip tooltip={disabled ? undefined : space.name}>
            {(triggerRef) => (
              <SidebarAvatar
                as="button"
                data-id={space.roomId}
                ref={triggerRef}
                size={folder ? '300' : '400'}
                onContextMenu={menu.triggerProps.onContextMenu}
                onTouchStart={menu.triggerProps.onTouchStart}
                onTouchEnd={menu.triggerProps.onTouchEnd}
                onTouchMove={menu.triggerProps.onTouchMove}
                onTouchCancel={menu.triggerProps.onTouchCancel}
                {...mobileTapActivation}
              >
                <SpaceAvatar
                  space={space}
                  renderFallback={() => (
                    <Text size={folder ? 'H6' : 'H4'}>{nameInitials(space.name, 2)}</Text>
                  )}
                />
              </SidebarAvatar>
            )}
          </SidebarItemTooltip>
          {unread && (
            <SidebarUnreadBadge
              highlight={unread.highlight > 0}
              count={unread.highlight > 0 ? unread.highlight : unread.total}
            />
          )}
          <ResponsiveMenu
            anchor={menu.anchor}
            position="Right"
            align="Start"
            requestClose={menu.close}
            menu={<SpaceMenu room={space} requestClose={menu.close} onUnpin={onUnpin} />}
          />
        </SidebarItemLeft>
      )}
    </RoomUnreadProvider>
  );
}

type OpenedSpaceFolderProps = {
  folder: ISidebarFolder;
  onClose: MouseEventHandler<HTMLButtonElement>;
  onFolderContextMenu?: MouseEventHandler<HTMLDivElement>;
  children?: ReactNode;
};
function OpenedSpaceFolder({
  folder,
  onClose,
  onFolderContextMenu,
  children,
}: Readonly<OpenedSpaceFolderProps>) {
  const isMobile = useScreenSizeContext() === ScreenSize.Mobile;
  const aboveTargetRef = useRef<HTMLDivElement>(null);
  const belowTargetRef = useRef<HTMLDivElement>(null);

  const spaceDraggable: SidebarDraggable = useMemo(() => ({ folder, open: true }), [folder]);

  const orderAbove = useDropTargetInstruction(
    spaceDraggable,
    aboveTargetRef,
    'reorder-above',
    !isMobile
  );
  const orderBelow = useDropTargetInstruction(
    spaceDraggable,
    belowTargetRef,
    'reorder-below',
    !isMobile
  );

  return (
    <SidebarFolder
      state="Open"
      data-drop-above={orderAbove === 'reorder-above'}
      data-drop-below={orderBelow === 'reorder-below'}
    >
      <SidebarFolderDropTarget ref={aboveTargetRef} position="Top" />
      <SidebarAvatar size="300" onContextMenu={onFolderContextMenu}>
        <IconButton data-id={folder.id} size="300" variant="Background" onClick={onClose}>
          {composerIcon(CaretUp, { weight: 'fill' })}
        </IconButton>
      </SidebarAvatar>
      {children}
      <SidebarFolderDropTarget ref={belowTargetRef} position="Bottom" />
    </SidebarFolder>
  );
}

type ClosedSpaceFolderProps = {
  folder: ISidebarFolder;
  selected: boolean;
  onOpen: MouseEventHandler<HTMLButtonElement>;
  onDragging: (dragItem?: SidebarDraggable) => void;
  disabled?: boolean;
  onFolderContextMenu?: MouseEventHandler<HTMLButtonElement>;
};
function ClosedSpaceFolder({
  folder,
  selected,
  onOpen,
  onDragging,
  disabled,
  onFolderContextMenu,
}: Readonly<ClosedSpaceFolderProps>) {
  const mx = useMatrixClient();
  const isMobile = useScreenSizeContext() === ScreenSize.Mobile;
  const handlerRef = useRef<HTMLDivElement>(null);

  const spaceDraggable: FolderDraggable = useMemo(() => ({ folder }), [folder]);
  useDraggableItem(spaceDraggable, handlerRef, onDragging, undefined, !isMobile);
  const dropState = useDropTarget(spaceDraggable, handlerRef, !isMobile);
  const dropType = dropState?.type;

  const tooltipName = folderDefaultDisplayName(mx, folder);

  return (
    <RoomsUnreadProvider rooms={folder.content}>
      {(unread) => (
        <SidebarItemLeft
          active={selected}
          ref={handlerRef}
          aria-disabled={disabled}
          data-drop-child={dropType === 'make-child'}
          data-drop-above={dropType === 'reorder-above'}
          data-drop-below={dropType === 'reorder-below'}
        >
          <SidebarItemTooltip tooltip={disabled ? undefined : tooltipName}>
            {(tooltipRef) => (
              <SidebarFolder
                data-id={folder.id}
                as="button"
                ref={tooltipRef}
                onClick={onOpen}
                onContextMenu={onFolderContextMenu}
              >
                {folder.content.map((sId) => {
                  const space = mx.getRoom(sId);
                  if (!space) return null;

                  return (
                    <SidebarAvatar key={sId} size="200" radii="300">
                      <SpaceAvatar
                        space={space}
                        renderFallback={() => (
                          <Text size="Inherit">
                            <b>{nameInitials(space.name, 2)}</b>
                          </Text>
                        )}
                      />
                    </SidebarAvatar>
                  );
                })}
              </SidebarFolder>
            )}
          </SidebarItemTooltip>
          {unread && (
            <SidebarUnreadBadge
              highlight={unread.highlight > 0}
              count={unread.highlight > 0 ? unread.highlight : unread.total}
            />
          )}
        </SidebarItemLeft>
      )}
    </RoomsUnreadProvider>
  );
}

type SpaceTabsProps = {
  scrollRef: RefObject<HTMLDivElement | null>;
};
export function SpaceTabs({ scrollRef }: Readonly<SpaceTabsProps>) {
  const navigate = useNavigate();
  const mx = useMatrixClient();
  const screenSize = useScreenSizeContext();
  const roomToParents = useAtomValue(roomToParentsAtom);
  const orphanSpaces = useOrphanSpaces(mx, allRoomsAtom, roomToParents);
  const [sidebarItems, localEchoSidebarItem] = useSidebarItems(orphanSpaces);
  const navToActivePath = useAtomValue(useNavToActivePathAtom());
  const [openedFolder, setOpenedFolder] = useAtom(useOpenedSidebarFolderAtom());
  const setLastSpaceId = useSetAtom(lastVisitedSpaceIdAtom);
  const [draggingItem, setDraggingItem] = useState<SidebarDraggable>();
  const folderMenu = useMenuAnchor<HTMLDivElement>();
  const [folderMenuTarget, setFolderMenuTarget] = useState<ISidebarFolder>();
  const [renameTargetFolder, setRenameTargetFolder] = useState<ISidebarFolder>();

  const handleFolderContextMenu = useCallback(
    (folder: ISidebarFolder): MouseEventHandler =>
      (evt) => {
        evt.preventDefault();
        setFolderMenuTarget(folder);
        // Opening, not toggling: right-clicking a second folder while the first
        // menu is open must move the menu, not close it.
        folderMenu.open(evt.currentTarget as HTMLElement);
      },
    [folderMenu]
  );

  const handleRenameFolderApply = useCallback(
    (folderId: string, rawName: string) => {
      const newItems = renameSidebarFolderItem(sidebarItems, folderId, rawName);
      const newSpacesContent = makeCinnySpacesContent(mx, newItems);
      localEchoSidebarItem(parseSidebar(mx, orphanSpaces, newSpacesContent));
      mx.setAccountData(CustomAccountDataEvent.CinnySpaces, newSpacesContent);
    },
    [mx, sidebarItems, orphanSpaces, localEchoSidebarItem]
  );

  useDnDMonitor(
    scrollRef,
    setDraggingItem,
    useCallback(
      (item, containerItem, instructionType) => {
        const newItems: SidebarItems = [];

        const matchDest = (sI: TSidebarItem, dI: SidebarDraggable): boolean => {
          if (typeof sI === 'string' && typeof dI === 'string') {
            return sI === dI;
          }
          if (typeof sI === 'object' && typeof dI === 'object') {
            return sI.id === dI.folder.id;
          }
          return false;
        };
        const itemAsFolderContent = (i: SidebarDraggable): string[] => {
          if (typeof i === 'string') {
            return [i];
          }
          if (i.spaceId) {
            return [i.spaceId];
          }
          return [...i.folder.content];
        };

        sidebarItems.forEach((i) => {
          const sameFolders =
            typeof item === 'object' &&
            typeof containerItem === 'object' &&
            item.folder.id === containerItem.folder.id;

          // remove draggable space from current position or folder
          if (!sameFolders && matchDest(i, item)) {
            if (typeof item === 'object' && item.spaceId) {
              const folderContent = item.folder.content.filter((s) => s !== item.spaceId);
              if (folderContent.length === 0) {
                // remove open state from local storage
                setOpenedFolder({ type: 'DELETE', value: item.folder.id });
                return;
              }
              newItems.push({
                ...item.folder,
                content: folderContent,
              });
            }
            return;
          }
          if (matchDest(i, containerItem)) {
            // we can make child only if
            // container item is space or closed folder
            if (instructionType === 'make-child') {
              const child: string[] = itemAsFolderContent(item);
              if (typeof containerItem === 'string') {
                const folder: ISidebarFolder = {
                  id: randomStr(),
                  content: [containerItem].concat(child),
                };
                newItems.push(folder);
                return;
              }
              newItems.push({
                ...containerItem.folder,
                content: containerItem.folder.content.concat(child),
              });
              return;
            }

            // drop inside opened folder
            // or reordering inside same folder
            if (typeof containerItem === 'object' && containerItem.spaceId) {
              const child = itemAsFolderContent(item);
              const newContent: string[] = [];
              containerItem.folder.content
                .filter((sId) => !child.includes(sId))
                .forEach((sId) => {
                  if (sId === containerItem.spaceId) {
                    if (instructionType === 'reorder-below') {
                      newContent.push(sId, ...child);
                    }
                    if (instructionType === 'reorder-above') {
                      newContent.push(...child, sId);
                    }
                    return;
                  }
                  newContent.push(sId);
                });
              const folder = {
                ...containerItem.folder,
                content: newContent,
              };

              newItems.push(folder);
              return;
            }

            // drop above or below space or closed/opened folder
            if (typeof item === 'string') {
              if (instructionType === 'reorder-below') newItems.push(i);
              newItems.push(item);
              if (instructionType === 'reorder-above') newItems.push(i);
            } else if (item.spaceId) {
              if (instructionType === 'reorder-above') {
                newItems.push(item.spaceId);
              }
              if (sameFolders && typeof i === 'object') {
                // remove from folder if placing around itself
                const newI = {
                  ...i,
                  content: i.content.filter((sId) => sId !== item.spaceId),
                };
                if (newI.content.length > 0) newItems.push(newI);
              } else {
                newItems.push(i);
              }
              if (instructionType === 'reorder-below') {
                newItems.push(item.spaceId);
              }
            } else {
              if (instructionType === 'reorder-below') newItems.push(i);
              newItems.push(item.folder);
              if (instructionType === 'reorder-above') newItems.push(i);
            }
            return;
          }
          newItems.push(i);
        });

        const newSpacesContent = makeCinnySpacesContent(mx, newItems);
        localEchoSidebarItem(parseSidebar(mx, orphanSpaces, newSpacesContent));
        mx.setAccountData(CustomAccountDataEvent.CinnySpaces, newSpacesContent);
      },
      [mx, sidebarItems, setOpenedFolder, localEchoSidebarItem, orphanSpaces]
    )
  );

  const selectedSpaceId = useSelectedSpace();

  const handleSpaceClick = (spaceId: string) => {
    setLastSpaceId(spaceId);
    const spacePath = getSpacePath(getCanonicalAliasOrRoomId(mx, spaceId));
    if (screenSize === ScreenSize.Mobile) {
      navigate(spacePath);
      return;
    }

    const activePath = navToActivePath.get(spaceId);
    if (activePath?.pathname.startsWith(spacePath)) {
      navigate(joinPathComponent(activePath));
      return;
    }

    navigate(getSpaceLobbyPath(getCanonicalAliasOrRoomId(mx, spaceId)));
  };

  const handleFolderToggle: MouseEventHandler<HTMLButtonElement> = (evt) => {
    const target = evt.currentTarget;
    const targetFolderId = target.getAttribute('data-id');
    if (!targetFolderId) return;

    setOpenedFolder({
      type: openedFolder.has(targetFolderId) ? 'DELETE' : 'PUT',
      value: targetFolderId,
    });
  };

  const handleUnpin = useCallback(
    (roomId: string) => {
      if (orphanSpaces.includes(roomId)) return;
      const newItems = sidebarItemWithout(sidebarItems, roomId);

      const newSpacesContent = makeCinnySpacesContent(mx, newItems);
      localEchoSidebarItem(parseSidebar(mx, orphanSpaces, newSpacesContent));
      mx.setAccountData(CustomAccountDataEvent.CinnySpaces, newSpacesContent);
    },
    [mx, sidebarItems, orphanSpaces, localEchoSidebarItem]
  );

  if (sidebarItems.length === 0) return null;
  return (
    <>
      <ResponsiveMenu
        anchor={folderMenu.anchor}
        position="Right"
        align="Start"
        requestClose={() => {
          folderMenu.close();
          setFolderMenuTarget(undefined);
        }}
        menu={
          folderMenuTarget && (
            <FolderMenu
              requestClose={() => {
                folderMenu.close();
                setFolderMenuTarget(undefined);
              }}
              onRename={() => setRenameTargetFolder(folderMenuTarget)}
            />
          )
        }
      />
      {renameTargetFolder && (
        <RenameFolderDialog
          mx={mx}
          folder={renameTargetFolder}
          onClose={() => setRenameTargetFolder(undefined)}
          onSave={(name) => {
            handleRenameFolderApply(renameTargetFolder.id, name);
            setRenameTargetFolder(undefined);
          }}
        />
      )}
      <SidebarStackSeparator />
      <SidebarStack>
        {sidebarItems.map((item) => {
          if (typeof item === 'object') {
            if (openedFolder.has(item.id)) {
              return (
                <OpenedSpaceFolder
                  key={item.id}
                  folder={item}
                  onClose={handleFolderToggle}
                  onFolderContextMenu={handleFolderContextMenu(item)}
                >
                  {item.content.map((sId) => {
                    const space = mx.getRoom(sId);
                    if (!space) return null;
                    return (
                      <SpaceTab
                        key={space.roomId}
                        space={space}
                        selected={space.roomId === selectedSpaceId}
                        onSelect={handleSpaceClick}
                        folder={item}
                        onDragging={setDraggingItem}
                        disabled={
                          typeof draggingItem === 'object'
                            ? draggingItem.spaceId === space.roomId
                            : false
                        }
                        onUnpin={orphanSpaces.includes(space.roomId) ? undefined : handleUnpin}
                      />
                    );
                  })}
                </OpenedSpaceFolder>
              );
            }

            return (
              <ClosedSpaceFolder
                key={item.id}
                folder={item}
                selected={!!selectedSpaceId && item.content.includes(selectedSpaceId)}
                onOpen={handleFolderToggle}
                onDragging={setDraggingItem}
                onFolderContextMenu={handleFolderContextMenu(item)}
                disabled={
                  typeof draggingItem === 'object' ? draggingItem.folder.id === item.id : false
                }
              />
            );
          }

          const space = mx.getRoom(item);
          if (!space) return null;

          return (
            <SpaceTab
              key={space.roomId}
              space={space}
              selected={space.roomId === selectedSpaceId}
              onSelect={handleSpaceClick}
              onDragging={setDraggingItem}
              disabled={typeof draggingItem === 'string' ? draggingItem === space.roomId : false}
              onUnpin={orphanSpaces.includes(space.roomId) ? undefined : handleUnpin}
            />
          );
        })}
      </SidebarStack>
    </>
  );
}
